import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, FileText, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DELIVERED_COPY = {
  fr: {
    orderLabel: { collective: "Match collectif", individual: "Match individuel" },
    loadError: "Impossible de charger vos analyses livrées.",
    feedbackThanks: "Merci pour votre retour",
    feedbackError: "Impossible d'envoyer votre avis.",
    eyebrow: "Bibliothèque",
    title: "Mes analyses livrées",
    subtitle: "Retrouvez ici toutes vos analyses terminées, leurs liens et vos retours.",
    emptyTitle: "Aucune analyse livrée pour le moment",
    emptyText: "Vos analyses apparaîtront ici dès qu'elles seront terminées.",
    backDashboard: "Retour au dashboard",
    delivered: "Livrée",
    orderedOn: "Commandée le",
    deliveredOn: "livrée le",
    viewAnalysis: "Voir mon analyse",
    close: "Fermer",
    details: "Détails",
    initialRequest: "Demande initiale",
    mathisMessage: "Message de Mathis",
    yourFeedback: "Votre avis",
    usefulQuestion: "L’analyse t’a été utile ?",
    commentPlaceholder: "Votre retour, ce qui vous a aidé...",
    sendFeedback: "Envoyer mon avis",
  },
  en: {
    orderLabel: { collective: "Team match", individual: "Individual match" },
    loadError: "Unable to load your delivered analyses.",
    feedbackThanks: "Thanks for your feedback",
    feedbackError: "Unable to send your feedback.",
    eyebrow: "Library",
    title: "My delivered analyses",
    subtitle: "Find all your completed analyses, links, dates and feedback here.",
    emptyTitle: "No delivered analysis yet",
    emptyText: "Your analyses will appear here as soon as they are completed.",
    backDashboard: "Back to dashboard",
    delivered: "Delivered",
    orderedOn: "Ordered on",
    deliveredOn: "delivered on",
    viewAnalysis: "View my analysis",
    close: "Close",
    details: "Details",
    initialRequest: "Initial request",
    mathisMessage: "Message from Mathis",
    yourFeedback: "Your feedback",
    usefulQuestion: "Was the analysis useful?",
    commentPlaceholder: "Your feedback, what helped you...",
    sendFeedback: "Send feedback",
  },
};

const orderLabel = (type, copy) => copy.orderLabel[type] || copy.orderLabel.individual;

const DeliveredAnalysesPage = () => {
  const { language } = useLanguage();
  const copy = DELIVERED_COPY[language] || DELIVERED_COPY.fr;
  const locale = language === "en" ? "en-US" : "fr-FR";
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [feedback, setFeedback] = useState({ rating: 5, comment: "" });
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (err) {
      toast.error(copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadError]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const delivered = useMemo(
    () => orders.filter((order) => order.status === "delivered").sort((a, b) => new Date(b.delivered_at || b.created_at) - new Date(a.delivered_at || a.created_at)),
    [orders]
  );

  const submitFeedback = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/feedback`, feedback);
      toast.success(copy.feedbackThanks);
      setFeedback({ rating: 5, comment: "" });
      await fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || copy.feedbackError);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-wide text-[#CCFF00] mb-2">{copy.eyebrow}</p>
            <h1 className="text-3xl sm:text-4xl font-black uppercase text-white">{copy.title}</h1>
            <p className="text-zinc-500 mt-2">{copy.subtitle}</p>
          </div>

          {loading ? (
            <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin mx-auto" />
          ) : delivered.length === 0 ? (
            <div className="bg-[#18181B] border border-white/10 p-8 text-center">
              <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-white font-bold mb-2">{copy.emptyTitle}</p>
              <p className="text-zinc-500 mb-5">{copy.emptyText}</p>
              <Link to="/dashboard" className="inline-flex items-center justify-center bg-[#CCFF00] text-black px-4 py-2 font-bold text-sm uppercase hover:bg-[#AACC00] transition-colors">{copy.backDashboard}</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {delivered.map((order) => (
                <div key={order.order_id} className="bg-[#18181B] border border-white/10 p-5 hover:border-white/20 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-green-500/20 text-green-400">{copy.delivered}</Badge>
                        <p className="text-white font-bold">{orderLabel(order.order_type, copy)}</p>
                      </div>
                      <p className="text-sm text-zinc-500">
                        {copy.orderedOn} {new Date(order.created_at).toLocaleDateString(locale)}
                        {order.delivered_at ? ` • ${copy.deliveredOn} ${new Date(order.delivered_at).toLocaleDateString(locale)}` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {order.analysis_link && (
                        <a href={order.analysis_link} target="_blank" rel="noopener noreferrer" className="bg-[#CCFF00] text-black px-3 py-2 text-xs font-bold uppercase hover:bg-[#AACC00] transition-colors inline-flex items-center gap-2">
                          <ExternalLink className="w-3 h-3" />
                          {copy.viewAnalysis}
                        </a>
                      )}
                      <button onClick={() => setExpanded(expanded === order.order_id ? null : order.order_id)} className="border border-white/20 text-white px-3 py-2 text-xs font-bold uppercase hover:bg-white/5 transition-colors">
                        {expanded === order.order_id ? copy.close : copy.details}
                      </button>
                    </div>
                  </div>

                  {expanded === order.order_id && (
                    <div className="mt-5 border-t border-white/10 pt-5 space-y-5">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase mb-1">{copy.initialRequest}</p>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{order.specific_request}</p>
                      </div>

                      {order.analyst_message && (
                        <div className="border border-[#CCFF00]/20 bg-[#CCFF00]/10 p-4">
                          <p className="text-xs text-zinc-500 uppercase mb-1">{copy.mathisMessage}</p>
                          <p className="text-sm text-white">{order.analyst_message}</p>
                        </div>
                      )}

                      {order.feedback ? (
                        <div className="border border-white/10 bg-black/20 p-4">
                          <p className="text-sm text-white font-bold">{copy.yourFeedback} : {order.feedback.rating}/5</p>
                          {order.feedback.comment && <p className="text-sm text-zinc-400 mt-1">{order.feedback.comment}</p>}
                        </div>
                      ) : (
                        <div className="border border-white/10 bg-black/20 p-4">
                          <p className="text-white font-bold mb-3">{copy.usefulQuestion}</p>
                          <div className="flex gap-2 mb-3">
                            {[1, 2, 3, 4, 5].map((note) => (
                              <button key={note} type="button" onClick={() => setFeedback({ ...feedback, rating: note })} className={note <= feedback.rating ? "text-[#CCFF00]" : "text-zinc-600"}>
                                <Star className="w-6 h-6 fill-current" />
                              </button>
                            ))}
                          </div>
                          <textarea value={feedback.comment} onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })} rows={3} className="w-full bg-[#0A0A0A] border border-white/20 text-white p-3 text-sm focus:border-[#CCFF00] focus:outline-none" placeholder={copy.commentPlaceholder} />
                          <button onClick={() => submitFeedback(order.order_id)} className="mt-3 bg-[#CCFF00] text-black px-4 py-2 text-xs font-bold uppercase hover:bg-[#AACC00]">
                            {copy.sendFeedback}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DeliveredAnalysesPage;
